import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CatRunningCanvas from '../CatRunningCanvas';
import useCatRunningGame from '../useCatRunningGame';
import catRunningSounds from '../game/sounds';
import {
  animateCount,
  ensureRewardStyles,
  getRouletteDuration,
  playRewardBurst,
  rainCoins,
} from '../game/rewardFx';
import magnetIcon from '../../../assets/cat-running/items/magnet.png';
import snackIcon from '../../../assets/cat-running/items/snack.png';
import shieldIcon from '../../../assets/cat-running/items/shield.png';
import trophyIcon from '../../../assets/cat-running/ui/final-point-trophy.png';
import CatRunningOutlineFrame from './CatRunningOutlineFrame';
import useCatRunningBgm from '../useCatRunningBgm';
import { CAT_RUNNER_MOCK_RUNTIME, CAT_RUNNER_PRE_ITEM_TYPES } from '../mockBackend';
import {
  getArmedFlagsFromPreItems,
  getNextItemUsePlan,
} from '../game/itemPricing';

const CONTINUE_COST = CAT_RUNNER_MOCK_RUNTIME.continueSnackCost;
const formatDistance = (distance) => `${distance.toLocaleString('ko-KR')}m`;
const formatPoint = (value) => `+${value.toLocaleString('ko-KR')}P`;

const buildFinalPointRows = ({ distanceM, churuCount }) => {
  const hundredMeterCount = Math.floor(distanceM / 100);
  const hundredMeterPoint = Math.floor(hundredMeterCount * 1.5);
  const fiveHundredMeterCount = Math.floor(distanceM / 500);
  const fiveHundredMeterPoint = fiveHundredMeterCount * 3;
  const churuUnitCount = Math.floor(Math.max(0, churuCount ?? 0) / 20);
  const churuPointCount = Math.floor(churuUnitCount * 2);
  const totalPoint = hundredMeterPoint + fiveHundredMeterPoint + churuPointCount;

  return {
    rows: [
      {
        key: 'distance-100',
        prefix: '누적',
        detail: `100m (${hundredMeterCount}회)`,
        accent: `+${hundredMeterCount}회`,
        point: formatPoint(hundredMeterPoint),
        pointValue: hundredMeterPoint,
      },
      {
        key: 'distance-500',
        prefix: '누적',
        detail: `500m (${fiveHundredMeterCount}회)`,
        accent: `+${fiveHundredMeterCount}회`,
        point: formatPoint(fiveHundredMeterPoint),
        pointValue: fiveHundredMeterPoint,
      },
      {
        key: 'churu-20',
        prefix: '츄르',
        detail: `(20 × ${churuUnitCount})`,
        accent: `+${churuPointCount}P`,
        point: formatPoint(churuPointCount),
        pointValue: churuPointCount,
      },
    ],
    totalPrefix: 'Total',
    totalDetail: formatDistance(distanceM),
    totalPoint: `${totalPoint.toLocaleString('ko-KR')}P`,
    totalValue: totalPoint,
  };
};

const SETTLE_ROW_STEPS = [
  { delay: 250, popFreq: 1050 },
  { delay: 700, popFreq: 1250 },
  { delay: 1150, popFreq: 1500 },
];
const SETTLE_TOTAL_DELAY = 1650;
const SETTLE_BURST_DELAY = SETTLE_TOTAL_DELAY + 320;
const SETTLE_COUNT_DURATION = 450;
const SETTLE_TOTAL_COUNT_DURATION = 700;

function CatRunningActiveItemTimer({ icon, seconds, alt }) {
  return (
    <div className="flex flex-col items-center">
      <img src={icon} alt={alt} className="h-[52px] w-[52px] object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]" />
      <div className="mt-1 min-w-[52px] rounded-[6px] bg-[rgba(30,30,30,0.82)] px-2 py-[3px] text-center">
        <span className="font-['Pretendard'] text-[16px] font-bold leading-none text-white">{seconds}s</span>
      </div>
    </div>
  );
}

function CatRunningBgmMuteButton({ muted, onToggle }) {
  return (
    <button
      type="button"
      className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-[rgba(28,28,28,0.88)] text-white shadow-[0_4px_12px_rgba(0,0,0,0.35)] backdrop-blur-[2px]"
      onClick={onToggle}
      aria-label={muted ? '배경음악 켜기' : '배경음악 끄기'}
    >
      {muted ? (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M11 5L6 9H3V15H6L11 19V5Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path d="M16 9L21 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M21 9L16 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      ) : (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M11 5L6 9H3V15H6L11 19V5Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M15.5 8.5C16.8 9.8 17.5 11.3 17.5 12C17.5 12.7 16.8 14.2 15.5 15.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M18 6C20.2 8.2 21.5 10.9 21.5 12C21.5 13.1 20.2 15.8 18 18"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}

function CatRunningItemActionButton({
  icon,
  label,
  snackCost = 0,
  isExhausted = false,
  canUse = false,
  isActive = false,
  onClick,
  disabled = false,
  alt,
}) {
  const isDisabledVisual = !canUse && !isActive;

  return (
    <button
      type="button"
      className={`pointer-events-auto flex h-[76px] w-[76px] flex-col items-center justify-center gap-[3px] rounded-full border border-white/20 bg-[rgba(28,28,28,0.88)] font-['Pretendard'] text-white shadow-[0_4px_12px_rgba(0,0,0,0.35)] backdrop-blur-[2px] disabled:cursor-default ${
        isDisabledVisual ? 'opacity-35 grayscale' : ''
      }`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <img src={icon} alt={alt} className="h-[34px] w-[34px] object-contain" />
      {isExhausted ? (
        <span className="text-[10px] font-bold leading-none text-white/70">완료</span>
      ) : snackCost > 0 ? (
        <span className="inline-flex items-center gap-[2px] text-[11px] font-bold leading-none">
          <img src={snackIcon} alt="" className="h-[14px] w-[14px] object-contain" />
          <span>{snackCost}개</span>
        </span>
      ) : (
        <span className="text-[11px] font-bold leading-none">{label}</span>
      )}
    </button>
  );
}

export default function CatRunningGameScreen({
  onBack,
  onContinueRound,
  onContinueByAd,
  onFinishRound,
  onUseItem,
  onItemUseCountChange,
  snackCount = 0,
  activeRound,
  activePreItemTypes = [],
  itemUseCounts = null,
  previewGameOver = false,
  continueAdLoading = false,
}) {
  const gameOptions = useMemo(
    () => ({
      runSeed: activeRound?.runSeed,
      spawnManifest: activeRound?.spawnManifest,
      startPaused: !previewGameOver,
    }),
    [activeRound?.runSeed, activeRound?.spawnManifest, previewGameOver],
  )
  const {
    hudState,
    renderState,
    worldRef,
    assetsRef,
    accumulatorRef,
    jump,
    revive,
    activateShield,
    activateMagnet,
    cancelShieldActivation,
    cancelMagnetActivation,
    getReplaySnapshot,
    setPaused,
  } =
    useCatRunningGame(gameOptions);
  const isGameOver = previewGameOver || hudState.gameStatus === 'gameover';
  const gameOverImageSrc = assetsRef.current.gameOver?.src;
  const [gameOverPhase, setGameOverPhase] = useState('idle');
  const [countdownValue, setCountdownValue] = useState(0);
  const [startCountdown, setStartCountdown] = useState(previewGameOver ? 0 : 3);
  const [isContinuing, setIsContinuing] = useState(false);
  const [isFinishingRound, setIsFinishingRound] = useState(false);
  const [isRoundSettled, setIsRoundSettled] = useState(false);
  const [usingItemType, setUsingItemType] = useState(null);
  const [shieldUseCount, setShieldUseCount] = useState(() =>
    Number(itemUseCounts?.[CAT_RUNNER_PRE_ITEM_TYPES.SHIELD] ?? 0),
  );
  const [magnetUseCount, setMagnetUseCount] = useState(() =>
    Number(itemUseCounts?.[CAT_RUNNER_PRE_ITEM_TYPES.MAGNET] ?? 0),
  );
  const { shieldArmed, magnetArmed } = getArmedFlagsFromPreItems(activePreItemTypes);
  const nextShieldPlan = getNextItemUsePlan({
    shieldArmed,
    magnetArmed,
    itemType: CAT_RUNNER_PRE_ITEM_TYPES.SHIELD,
    useCount: shieldUseCount,
    snackCount,
  });
  const nextMagnetPlan = getNextItemUsePlan({
    shieldArmed,
    magnetArmed,
    itemType: CAT_RUNNER_PRE_ITEM_TYPES.MAGNET,
    useCount: magnetUseCount,
    snackCount,
  });
  const gameOverRef = useRef(null);
  const autoFinishTriggeredRef = useRef(false);
  const [settleDistance, setSettleDistance] = useState(0);
  const [settleRowValues, setSettleRowValues] = useState([0, 0, 0]);
  const [settleRowShown, setSettleRowShown] = useState([false, false, false]);
  const [settleTotal, setSettleTotal] = useState(0);
  const [settleTotalShown, setSettleTotalShown] = useState(false);
  const [settleTotalPop, setSettleTotalPop] = useState(false);
  const summaryDistanceM = previewGameOver && hudState.distance === 0 ? 1840 : hudState.distance;
  const summaryChuruCount = previewGameOver && hudState.churuCount === 0 ? 57 : hudState.churuCount;
  const finalPointSummary = useMemo(
    () => buildFinalPointRows({ distanceM: summaryDistanceM, churuCount: summaryChuruCount }),
    [summaryChuruCount, summaryDistanceM],
  );
  const finalPointSummaryRef = useRef(finalPointSummary);
  finalPointSummaryRef.current = finalPointSummary;
  const canAffordContinue = snackCount >= CONTINUE_COST;
  const canShowContinueButton = !hudState.reviveUsed;
  const isCountdownActive = countdownValue > 0;
  const isStartCountdownActive = startCountdown > 0;
  const isTerminalGameOver = isGameOver && hudState.reviveUsed;
  const shieldSeconds = (Math.max(0, hudState.invincibleMs) / 1000).toFixed(1);
  const isShieldActive = hudState.invincibleMs > 0;
  const magnetSeconds = (Math.max(0, hudState.magnetMs) / 1000).toFixed(1);
  const isMagnetActive = hudState.magnetMs > 0;
  const canShowShieldButton = !isGameOver;
  const canShowMagnetButton = !isGameOver;
  const canUseShield = !isShieldActive && nextShieldPlan.canUse;
  const canUseMagnet = !isMagnetActive && nextMagnetPlan.canUse;
  // 카운트다운 중에도 세션을 유지한다. false로 두면 start unlock 직후 stop()이 걸려
  // "잠깐 나오다 끊김"이 발생하고, 이후 점프 재생도 불안정해진다.
  const shouldPlayBgm = !previewGameOver && !isGameOver;
  const { muted: isBgmMuted, toggleMuted: toggleBgmMuted, unlockFromUserGesture } = useCatRunningBgm({
    shouldPlay: shouldPlayBgm,
    worldRef,
  });

  const handleJump = useCallback(() => {
    unlockFromUserGesture();
    jump();
  }, [jump, unlockFromUserGesture]);

  const handleCountdownPointerDown = useCallback(() => {
    // 카운트다운 중 터치로 모바일 오디오 자동재생 잠금 해제
    unlockFromUserGesture();
  }, [unlockFromUserGesture]);

  useEffect(() => {
    if (!isGameOver) {
      setGameOverPhase('idle');
      return undefined;
    }

    setGameOverPhase('intro');

    const revealTimer = window.setTimeout(() => {
      setGameOverPhase('summary');
    }, 2300);

    return () => {
      window.clearTimeout(revealTimer);
    };
  }, [isGameOver]);

  useEffect(() => {
    if (startCountdown <= 0) return undefined;

    ensureRewardStyles();

    if (startCountdown === 1) {
      const goTimer = window.setTimeout(() => {
        setStartCountdown(0);
        setPaused(false);
      }, 900);
      return () => window.clearTimeout(goTimer);
    }

    const nextTimer = window.setTimeout(() => {
      setStartCountdown((current) => Math.max(0, current - 1));
    }, 900);
    return () => window.clearTimeout(nextTimer);
  }, [startCountdown, setPaused]);

  useEffect(() => {
    if (!isGameOver) {
      setSettleDistance(0);
      return undefined;
    }

    setSettleDistance(0);
    const target = Math.floor(summaryDistanceM);
    const duration = getRouletteDuration(target);
    let lastTickAt = 0;
    let cancelCount = null;
    let cancelRain = null;

    const startTimer = window.setTimeout(() => {
      cancelCount = animateCount(
        target,
        duration,
        (value, progress) => {
          setSettleDistance(value);
          const now = performance.now();
          if (progress < 1 && now - lastTickAt > 55) {
            catRunningSounds.tick(900 + progress * 700);
            lastTickAt = now;
          }
        },
        () => catRunningSounds.ding(),
      );
      cancelRain = rainCoins(gameOverRef.current, Math.floor(target / 100), duration);
    }, 450);

    return () => {
      window.clearTimeout(startTimer);
      if (cancelCount) cancelCount();
      if (cancelRain) cancelRain();
    };
  }, [isGameOver, summaryDistanceM]);

  useEffect(() => {
    if (!isGameOver) {
      setSettleRowValues([0, 0, 0]);
      setSettleRowShown([false, false, false]);
      setSettleTotal(0);
      setSettleTotalShown(false);
      setSettleTotalPop(false);
      return undefined;
    }
    if (gameOverPhase !== 'summary') return undefined;

    ensureRewardStyles();
    const summary = finalPointSummaryRef.current;
    const rowTargets = summary.rows.map((row) => row.pointValue);
    const totalTarget = summary.totalValue;
    const timers = [];
    const cancels = [];

    setSettleRowValues([0, 0, 0]);
    setSettleRowShown([false, false, false]);
    setSettleTotal(0);
    setSettleTotalShown(false);
    setSettleTotalPop(false);

    SETTLE_ROW_STEPS.forEach((stepInfo, index) => {
      timers.push(
        window.setTimeout(() => {
          catRunningSounds.pop(stepInfo.popFreq);
          setSettleRowShown((prev) => {
            const next = [...prev];
            next[index] = true;
            return next;
          });
          cancels.push(
            animateCount(rowTargets[index], SETTLE_COUNT_DURATION, (value) => {
              setSettleRowValues((prev) => {
                const next = [...prev];
                next[index] = value;
                return next;
              });
            }),
          );
        }, stepInfo.delay),
      );
    });

    timers.push(
      window.setTimeout(() => {
        catRunningSounds.ding();
        setSettleTotalShown(true);
        setSettleTotalPop(true);
        cancels.push(
          animateCount(totalTarget, SETTLE_TOTAL_COUNT_DURATION, (value) => setSettleTotal(value)),
        );
      }, SETTLE_TOTAL_DELAY),
    );

    timers.push(
      window.setTimeout(() => {
        const tier = playRewardBurst(gameOverRef.current, totalTarget);
        if (tier > 0) catRunningSounds.fanfare(tier);
      }, SETTLE_BURST_DELAY),
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      cancels.forEach((cancel) => cancel && cancel());
    };
  }, [isGameOver, gameOverPhase]);

  useEffect(() => {
    if (itemUseCounts?.[CAT_RUNNER_PRE_ITEM_TYPES.SHIELD] !== undefined) {
      setShieldUseCount(Number(itemUseCounts[CAT_RUNNER_PRE_ITEM_TYPES.SHIELD]));
    }
    if (itemUseCounts?.[CAT_RUNNER_PRE_ITEM_TYPES.MAGNET] !== undefined) {
      setMagnetUseCount(Number(itemUseCounts[CAT_RUNNER_PRE_ITEM_TYPES.MAGNET]));
    }
  }, [itemUseCounts]);

  useEffect(() => {
    if (!isCountdownActive) return undefined;

    if (countdownValue === 1) {
      const reviveTimer = window.setTimeout(() => {
        setCountdownValue(0);
        revive();
      }, 900);

      return () => {
        window.clearTimeout(reviveTimer);
      };
    }

    const countdownTimer = window.setTimeout(() => {
      setCountdownValue((current) => Math.max(0, current - 1));
    }, 900);

    return () => {
      window.clearTimeout(countdownTimer);
    };
  }, [countdownValue, isCountdownActive, revive]);

  const continueRound = useCallback(async (continueRequest) => {
    setIsContinuing(true);
    try {
      const continued = await onContinueRound?.(continueRequest);
      if (continued) setCountdownValue(3);
      return continued;
    } finally {
      setIsContinuing(false);
    }
  }, [onContinueRound]);

  const handleContinue = async () => {
    if (!isGameOver || hudState.reviveUsed || isCountdownActive || isContinuing || isFinishingRound || continueAdLoading) return;

    const continueRequest = {
      deathDistanceM: hudState.distance,
      deathChuruCount: hudState.churuCount,
    };

    if (!canAffordContinue) {
      setIsContinuing(true);
      try {
        const continued = await onContinueByAd?.(continueRequest);
        if (continued) setCountdownValue(3);
      } finally {
        setIsContinuing(false);
      }
      return;
    }

    await continueRound(continueRequest);
  };

  const finishRound = useCallback(async ({ navigateHome = false } = {}) => {
    if (isFinishingRound || !activeRound?.roundToken) return false;

    setIsFinishingRound(true);
    try {
      const { createPlayEndRequest, ...replaySnapshot } = getReplaySnapshot();
      const finished = await onFinishRound?.(
        {
          roundToken: activeRound.roundToken,
          mode: activeRound.mode ?? 'SINGLE',
          savedAt: new Date().toISOString(),
          preItems: activePreItemTypes,
          ...replaySnapshot,
          playEndRequest: createPlayEndRequest(activeRound.roundToken),
        },
        { preserveActiveRound: !navigateHome },
      );
      if (finished) {
        setIsRoundSettled(true);
        if (navigateHome) onBack?.();
      }
      return finished;
    } finally {
      setIsFinishingRound(false);
    }
  }, [activePreItemTypes, activeRound?.mode, activeRound?.roundToken, getReplaySnapshot, isFinishingRound, onBack, onFinishRound]);

  useEffect(() => {
    if (!isTerminalGameOver || isRoundSettled || autoFinishTriggeredRef.current) return undefined;

    autoFinishTriggeredRef.current = true;
    finishRound();

    return undefined;
  }, [finishRound, isRoundSettled, isTerminalGameOver]);

  const handleFinishRound = async () => {
    if (isRoundSettled) {
      onBack?.();
      return;
    }

    await finishRound({ navigateHome: true });
  };

  const handleShield = async () => {
    if (!canUseShield || isGameOver) return;

    // 낙관적 적용: 클릭 즉시 게임에 효과 반영 후 API 호출
    const didActivateShield = activateShield();
    if (!didActivateShield) return;

    const nextShieldUseCount = shieldUseCount + 1;
    setShieldUseCount(nextShieldUseCount);
    onItemUseCountChange?.(CAT_RUNNER_PRE_ITEM_TYPES.SHIELD, nextShieldUseCount);

    setUsingItemType(CAT_RUNNER_PRE_ITEM_TYPES.SHIELD);
    const isItemUseConfirmed = await onUseItem?.(CAT_RUNNER_PRE_ITEM_TYPES.SHIELD);
    setUsingItemType(null);

    if (!isItemUseConfirmed) {
      // API 실패 시 되돌리기
      cancelShieldActivation();
      setShieldUseCount(shieldUseCount);
      onItemUseCountChange?.(CAT_RUNNER_PRE_ITEM_TYPES.SHIELD, shieldUseCount);
    }
  };

  const handleMagnet = async () => {
    if (!canUseMagnet || isGameOver) return;

    // 낙관적 적용: 클릭 즉시 게임에 효과 반영 후 API 호출
    const didActivateMagnet = activateMagnet();
    if (!didActivateMagnet) return;

    const nextMagnetUseCount = magnetUseCount + 1;
    setMagnetUseCount(nextMagnetUseCount);
    onItemUseCountChange?.(CAT_RUNNER_PRE_ITEM_TYPES.MAGNET, nextMagnetUseCount);

    setUsingItemType(CAT_RUNNER_PRE_ITEM_TYPES.MAGNET);
    const isItemUseConfirmed = await onUseItem?.(CAT_RUNNER_PRE_ITEM_TYPES.MAGNET);
    setUsingItemType(null);

    if (!isItemUseConfirmed) {
      // API 실패 시 되돌리기
      cancelMagnetActivation();
      setMagnetUseCount(magnetUseCount);
      onItemUseCountChange?.(CAT_RUNNER_PRE_ITEM_TYPES.MAGNET, magnetUseCount);
    }
  };

  return (
    <div className="min-h-screen bg-[#652419]">
      <div className="mx-auto flex h-[100dvh] min-h-0 w-full max-w-[500px] flex-col overflow-hidden bg-[#652419] shadow-[0_24px_80px_rgba(0,0,0,0.32)]">
        <div className="grid grid-cols-2 gap-2 bg-black/70 px-4 text-white">
          <div className="rounded-[16px] bg-white/8 px-3 py-3 text-center">
            <p className="m-0 font-['Pretendard'] text-[11px] font-bold uppercase tracking-[0.08em] text-white">
              거리
            </p>
            <p className="m-0 mt-1 font-['Pretendard'] text-[20px] font-extrabold leading-none">
              {formatDistance(hudState.distance)}
            </p>
          </div>
          <div className="rounded-[16px] bg-white/8 px-3 py-3 text-center">
            <p className="m-0 font-['Pretendard'] text-[11px] font-bold uppercase tracking-[0.08em] text-white">
              츄르
            </p>
            <p className="m-0 mt-1 font-['Pretendard'] text-[20px] font-extrabold leading-none">
              {hudState.churuCount}개
            </p>
          </div>
        </div>

        <div className="relative min-h-0 flex-1">
          <div className="relative overflow-hidden border border-black/10 bg-black/10">
            {isCountdownActive && (
              <div
                className="fixed inset-0 z-[300] flex flex-col items-center justify-center bg-black/55 text-white"
                onPointerDown={handleCountdownPointerDown}
              >
                <div className="-translate-y-5 text-center">
                  <p className="m-0 font-['Pretendard'] text-[15px] font-bold tracking-[0.04em] text-white/80">
                    준비하세요!
                  </p>
                  <p className="m-0 mt-3 font-['DungGeunMo'] text-[72px] leading-none text-white">{countdownValue}</p>
                </div>
              </div>
            )}
            {isStartCountdownActive && !isGameOver && (
              <div
                className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/55 text-white"
                onPointerDown={handleCountdownPointerDown}
              >
                <div className="-translate-y-5 text-center">
                  <p className="m-0 font-['Pretendard'] text-[15px] font-bold tracking-[0.04em] text-white/80">
                    준비하세요!
                  </p>
                  <p
                    key={startCountdown}
                    className="m-0 mt-3 font-['DungGeunMo'] text-[88px] leading-none text-white cr-cf-total-pop"
                  >
                    {startCountdown}
                  </p>
                </div>
              </div>
            )}
            <CatRunningCanvas worldRef={worldRef} assetsRef={assetsRef} accumulatorRef={accumulatorRef} onJump={handleJump} />
            {!isGameOver && (isShieldActive || isMagnetActive) && (
              <div className="pointer-events-none absolute inset-x-0 top-[10%] z-30 flex flex-row items-center justify-center gap-6">
                {isMagnetActive && (
                  <CatRunningActiveItemTimer icon={magnetIcon} seconds={magnetSeconds} alt="자석 활성" />
                )}
                {isShieldActive && (
                  <CatRunningActiveItemTimer icon={shieldIcon} seconds={shieldSeconds} alt="방패 활성" />
                )}
              </div>
            )}
          </div>
          {(canShowMagnetButton || canShowShieldButton) && !isGameOver && (
            <div className="pointer-events-none absolute inset-x-0 bottom-5 z-40 flex justify-center gap-8">
              {canShowShieldButton && (
                <CatRunningItemActionButton
                  icon={shieldIcon}
                  label="방패"
                  alt=""
                  snackCost={isShieldActive ? 0 : nextShieldPlan.snackCost}
                  isExhausted={nextShieldPlan.isExhausted}
                  canUse={canUseShield}
                  isActive={isShieldActive}
                  onClick={handleShield}
                  disabled={!canUseShield || usingItemType !== null || isShieldActive}
                />
              )}
              {canShowMagnetButton && (
                <CatRunningItemActionButton
                  icon={magnetIcon}
                  label="자석"
                  alt=""
                  snackCost={isMagnetActive ? 0 : nextMagnetPlan.snackCost}
                  isExhausted={nextMagnetPlan.isExhausted}
                  canUse={canUseMagnet}
                  isActive={isMagnetActive}
                  onClick={handleMagnet}
                  disabled={!canUseMagnet || usingItemType !== null || isMagnetActive}
                />
              )}
            </div>
          )}
          {!isGameOver && (
            <div className="pointer-events-none absolute bottom-5 right-4 z-40">
              <CatRunningBgmMuteButton muted={isBgmMuted} onToggle={toggleBgmMuted} />
            </div>
          )}
          {!renderState.backgroundReady && (
            <p className="m-0 px-1 pt-3 text-center font-['Pretendard'] text-[12px] font-bold text-[#36513C]">
              forest 배경 로딩 중...
            </p>
          )}
          {renderState.backgroundReady && !renderState.characterReady && (
            <p className="m-0 px-1 pt-3 text-center font-['Pretendard'] text-[12px] font-bold text-[#36513C]">
              캐릭터 에셋 로딩 중...
            </p>
          )}
          {renderState.backgroundReady && renderState.characterReady && !renderState.obstacleReady && (
            <p className="m-0 px-1 pt-3 text-center font-['Pretendard'] text-[12px] font-bold text-[#36513C]">
              장애물 에셋 로딩 중...
            </p>
          )}
          {renderState.backgroundReady &&
            renderState.characterReady &&
            renderState.obstacleReady &&
            !renderState.itemReady && (
              <p className="m-0 px-1 pt-3 text-center font-['Pretendard'] text-[12px] font-bold text-[#36513C]">
                츄르 에셋 로딩 중...
              </p>
            )}
          {renderState.backgroundReady &&
            renderState.characterReady &&
            renderState.obstacleReady &&
            renderState.itemReady &&
            !renderState.uiReady && (
              <p className="m-0 px-1 pt-3 text-center font-['Pretendard'] text-[12px] font-bold text-[#36513C]">
                결과 UI 에셋 로딩 중...
              </p>
            )}
          {isGameOver && (
            <div
              ref={gameOverRef}
              className="absolute inset-0 z-[200] touch-pan-y overflow-y-scroll overscroll-contain bg-[linear-gradient(180deg,rgba(10,18,28,0.12)_0%,rgba(10,18,28,0.26)_100%)] px-3 pb-[calc(2.5rem+env(safe-area-inset-bottom))] pt-6 text-white"
              style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
            >
              <div
                className="absolute left-1/2 w-[260px] -translate-x-1/2 text-center transition-all duration-700 ease-out"
                style={{
                  top: gameOverPhase === 'intro' ? '50%' : 50,
                  transform: gameOverPhase === 'intro' ? 'translate(-50%, -50%)' : 'translateX(-50%)',
                }}
              >
                {gameOverImageSrc ? (
                  <img src={gameOverImageSrc} alt="GAME OVER" className="mx-auto w-[200px] max-w-full" />
                ) : (
                  <p className="m-0 font-['Pretendard'] text-[32px] font-extrabold">GAME OVER</p>
                )}
                <p className="m-0 text-center font-['DungGeunMo'] text-6xl font-normal text-white">
                  {formatDistance(settleDistance)}
                </p>
              </div>

              <div
                className={`relative mx-0 mt-[13rem] transition-all duration-500 ease-out ${
                  gameOverPhase === 'summary'
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-16 opacity-0 pointer-events-none'
                }`}
                style={{ transitionDelay: gameOverPhase === 'summary' ? '0ms' : '0ms' }}
              >
                <CatRunningOutlineFrame
                  className="mx-auto w-full"
                  contentClassName="box-border w-full px-5 py-6"
                  fillClassName="bg-[rgba(15,31,45,0.55)] backdrop-blur-[2px]"
                  lineClassName="bg-white"
                >
                  <div className="flex items-center justify-center gap-[10px] pt-5">
                    <img src={trophyIcon} alt="" className="h-[32px] w-[32px] shrink-0 object-contain" />
                    <span className="font-['DungGeunMo'] text-[18px] leading-6 tracking-[-0.02em] text-white">
                      최종 포인트
                    </span>
                  </div>

                  <div className="mt-[10px] flex flex-col divide-y divide-white/80 px-4">
                    {finalPointSummary.rows.map((row, index) => (
                      <div
                        key={row.key}
                        className="flex w-full items-center justify-between pt-2 pb-2"
                        style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.72)',
                          opacity: settleRowShown[index] ? 1 : 0,
                          transform: settleRowShown[index] ? 'translateX(0)' : 'translateX(-16px)',
                          transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
                        }}
                      >
                        <div className="flex min-w-0 flex-1 items-center px-[10px]">
                          <div className="flex w-[50px] shrink-0 items-center justify-center">
                            <span className="font-['DungGeunMo'] text-[16px] leading-[1.4] text-white">{row.prefix}</span>
                          </div>
                          <div className="ml-[10px] min-w-0 flex-1 truncate font-['DungGeunMo'] text-[16px] leading-[1.4]">
                            <span className="text-white">
                              {row.key === 'churu-20' ? `(${Math.max(0, summaryChuruCount ?? 0)}개) ` : `${row.detail} `}
                            </span>
                            {row.point !== '+0P' && <span className="text-[#FFBC2B]">{row.accent}</span>}
                          </div>

                        </div>
                        <div className="shrink-0 px-[17px] text-right">
                          <span className="font-['DungGeunMo'] text-[16px] leading-[1.4] text-white">
                            +{(settleRowValues[index] ?? 0).toLocaleString('ko-KR')}P
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    className="flex w-full items-center justify-between pt-4 pb-4 px-[16px]"
                    style={{
                      opacity: settleTotalShown ? 1 : 0,
                      transform: settleTotalShown ? 'scale(1)' : 'scale(0.85)',
                      transformOrigin: 'center',
                      transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
                    }}
                  >
                    <div className="flex min-w-0 flex-1 items-center px-[10px]">
                      <div className="flex w-[50px] shrink-0 items-center justify-center">
                        <span className="font-['DungGeunMo'] text-[16px] leading-[1.4] text-white">
                          {finalPointSummary.totalPrefix}
                        </span>
                      </div>
                      <span className="ml-[10px] min-w-0 flex-1 truncate font-['DungGeunMo'] text-[16px] leading-[1.4] text-white">
                        {finalPointSummary.totalDetail}
                      </span>
                    </div>
                    <div className="shrink-0 px-[17px] text-right">
                      <span
                        className={`inline-block font-['DungGeunMo'] text-[16px] leading-[1.4] text-white${
                          settleTotalPop ? ' cr-cf-total-pop' : ''
                        }`}
                      >
                        {settleTotal.toLocaleString('ko-KR')}P
                      </span>
                    </div>
                  </div>
                </CatRunningOutlineFrame>

                {/* {!isTerminalGameOver && (
                <div className="mx-auto mt-3 w-full max-w-[340px]">
                  <div className="mx-auto h-[42px] px-5 w-fit bg-black rounded-[5px] flex justify-center items-center gap-2">
                    <img src={snackIcon} alt="" className="h-[14px] w-[16px] object-contain" />
                    <span className="font-['Pretendard'] text-[16px] font-bold leading-none text-white">
                      남은 간식 {snackCount}개
                    </span>
                  </div>
                </div>
                )} */}

                <div className="mt-3 flex flex-col gap-3">
                  {canShowContinueButton && (
                    <button
                      type="button"
                      className="flex h-[60px] items-center justify-center rounded-[8px] border-0 bg-[#8E67E8] font-['Pretendard'] text-[16px] font-bold text-white shadow-[0_10px_24px_rgba(74,41,143,0.35)] disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={handleContinue}
                      disabled={isCountdownActive || isContinuing || isFinishingRound || continueAdLoading}
                    >
                      {continueAdLoading ? (
                        <span>광고 시청 중</span>
                      ) : canAffordContinue ? (
                        <>
                          <span>이어하기</span>
                          <img src={snackIcon} alt="" className="ml-2 mr-0.5 h-[15px] w-[17px] object-contain" />
                          <span className="text-[12px]">x{CONTINUE_COST}</span>
                        </>
                      ) : (
                        <span>광고 보고 이어하기</span>
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    className="h-[60px] rounded-[8px] border-0 bg-black font-['Pretendard'] text-[16px] font-bold text-white"
                    onClick={handleFinishRound}
                    disabled={isFinishingRound || isContinuing || continueAdLoading}
                  >
                    {isTerminalGameOver ? '홈으로' : '보상받고 마무리'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {isCountdownActive && (
            <div className="hidden absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/55 text-white">
              <p className="m-0 font-['Pretendard'] text-[15px] font-bold tracking-[0.04em] text-white/80">
                준비하세요!
              </p>
              <p className="m-0 mt-3 font-['DungGeunMo'] text-[72px] leading-none text-white">{countdownValue}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
